import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { getProjects, createProject, updateProject, deleteProject } from "../services/api";

const defaultForm = { title: "", category: "SDTM", thumbnail: "", problem: "", approach: "", tools: "", outcome: "", learnings: "", order: 0 };

export default function AdminProjects() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getProjects();
      setList(data);
    } catch (e) {
      toast({ title: "Load failed", description: e?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const updated = await updateProject(editing.id, { ...form, order: Number(form.order || 0) });
        setList((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast({ title: "Updated", description: updated.title });
      } else {
        const created = await createProject({ ...form, order: Number(form.order || 0) });
        setList((prev) => [created, ...prev]);
        toast({ title: "Created", description: created.title });
      }
      setForm(defaultForm);
      setEditing(null);
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e?.message });
    }
  };

  const onEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
  };

  const onDelete = async (item) => {
    if (!confirm(`Delete ${item.title}?`)) return;
    try {
      await deleteProject(item.id);
      setList((prev) => prev.filter((p) => p.id !== item.id));
      toast({ title: "Deleted", description: item.title });
    } catch (e) {
      toast({ title: "Delete failed", description: e?.response?.data?.detail || e?.message });
    }
  };

  const signOut = () => {
    localStorage.removeItem("adminToken");
    toast({ title: "Signed out" });
    window.location.href = "/";
  };

  const categories = useMemo(() => Array.from(new Set(list.map((p) => p.category))).sort(), [list]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin · Projects</h1>
          <p className="text-slate-500 text-sm mt-1">Protected by a simple token guard.</p>
        </div>
        <Button variant="outline" onClick={signOut}>Sign out</Button>
      </div>

      <Card className="mt-6">
        <CardHeader><CardTitle>{editing ? `Edit: ${editing.title}` : "Add Project"}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm">Title</label>
              <Input required name="title" value={form.title} onChange={onChange} />
            </div>
            <div>
              <label className="text-sm">Category</label>
              <Input required name="category" value={form.category} onChange={onChange} placeholder="SDTM / DCT / Local Lab / Training" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm">Thumbnail URL</label>
              <Input name="thumbnail" value={form.thumbnail} onChange={onChange} placeholder="https://...webp" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm">Problem</label>
              <Textarea name="problem" value={form.problem} onChange={onChange} rows={2} />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm">Approach</label>
              <Textarea name="approach" value={form.approach} onChange={onChange} rows={2} />
            </div>
            <div>
              <label className="text-sm">Tools/Standards</label>
              <Input name="tools" value={form.tools} onChange={onChange} />
            </div>
            <div>
              <label className="text-sm">Outcome</label>
              <Input name="outcome" value={form.outcome} onChange={onChange} />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm">Key Learnings</label>
              <Textarea name="learnings" value={form.learnings} onChange={onChange} rows={2} />
            </div>
            <div>
              <label className="text-sm">Order (higher ranks first)</label>
              <Input name="order" value={form.order} onChange={onChange} type="number" />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" className="bg-sky-600 text-white">{editing ? "Save Changes" : "Create Project"}</Button>
              {editing && <Button type="button" variant="outline" onClick={() => { setEditing(null); setForm(defaultForm); }}>Cancel</Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader><CardTitle>Projects ({list.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.title}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-700 border">{p.category}</Badge></TableCell>
                  <TableCell>{p.order ?? 0}</TableCell>
                  <TableCell className="space-x-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(p)}>Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => onDelete(p)}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}